<script setup lang="ts">
import { ref, watch } from 'vue';
import { cloneDeep } from 'lodash';

import { exhaustiveGuard } from '@libs/utils';

import { FerryFilterType } from './FerryFilters.model';
import type { FerryFilters, FerryFilterEvents } from './FerryFilters.model';
import FerryFiltersView from './FerryFiltersView.vue';

interface FerryFiltersLogicProps {
  data: FerryFilters;
  shouldUpdateOnClick?: boolean;
}

const props = defineProps<FerryFiltersLogicProps>();

interface FerryFiltersLogicEmits {
  (eventName: 'trigger-action', context: FerryFilterEvents): void;
  (eventName: 'new-actions', context: FerryFilterEvents[]): void;
}

const emit = defineEmits<FerryFiltersLogicEmits>();

const userFilters = ref<FerryFilters | null>(null);

function handleAction(action: FerryFilterEvents): void {
  if (props.shouldUpdateOnClick) {
    updateFilter(action);
    emit('new-actions', filterEventsActions);
  } else {
    emit('trigger-action', action);
  }
}

const filterEventsActions: FerryFilterEvents[] = [];

function storeFilterAction(action: FerryFilterEvents): void {
  if (action.filterType !== FerryFilterType.Carriers) {
    const indexOfAction = filterEventsActions.findIndex(
      (filterEventAction) => filterEventAction.filterType === action.filterType
    );

    if (indexOfAction !== -1) {
      filterEventsActions[indexOfAction] = action;
    } else {
      filterEventsActions.push(action);
    }
  } else {
    const indexOfAction = filterEventsActions.findIndex(
      (filterEventAction) =>
        filterEventAction.filterType === action.filterType &&
        filterEventAction.data.selected === action.data.selected
    );

    if (indexOfAction !== -1) {
      filterEventsActions.splice(indexOfAction, 1);
    } else {
      filterEventsActions.push(action);
    }
  }
}

function updateFilter(action: FerryFilterEvents): void {
  storeFilterAction(action);
  updateModalFilterVaules(action);
}

function updateModalFilterVaules(action: FerryFilterEvents): void {
  switch (action.filterType) {
    case FerryFilterType.ArrivalTime: {
      const filter = userFilters.value?.arrivalTime.filters[0];

      if (filter) {
        filter.options.initialValue = action.data.range;
      }

      break;
    }
    case FerryFilterType.Carriers: {
      const filters = userFilters.value?.carriers.data[0]?.filters;

      if (filters) {
        const selected = action.data.selected;
        const filterToUpdate = filters?.find(
          (filter) => filter.options.code === selected
        );

        if (filterToUpdate) {
          filterToUpdate.options.checked = !filterToUpdate.options.checked;
        }
      }

      break;
    }

    case FerryFilterType.DepartureTime: {
      const filter = userFilters.value?.departureTime.filters[0];

      if (filter) {
        filter.options.initialValue = action.data.range;
      }

      break;
    }

    default:
      exhaustiveGuard(action, `filterType not supported`);
  }
}

function clearStoredActions(): void {
  filterEventsActions.length = 0;
}

watch(
  () => props.data,
  () => {
    userFilters.value = cloneDeep(props.data);
    clearStoredActions();
  },
  { immediate: true }
);
</script>

<template>
  <FerryFiltersView
    v-if="userFilters"
    :data="userFilters"
    @update="handleAction"
  />
</template>
